import camelcase from 'camelcase';

import {
    DEFAULT_VALUE_RENTAL_PLAN,
    DEFAULT_VALUE_IN_STOCK,
    DEFAULT_VALUE_MIXABLE,
} from 'ggApp/modules/filter/constants';

import { sendStructEvent } from '../../api';

type RangeFilterValue = {
    min: number;
    max: number;
};

type RangeFilter = {
    label: string;
    values: RangeFilterValue[];
};

type MatchFilter = {
    label: string;
    values: number[];
};

type FilterObjectType = {
    store: number;
    orderID?: string;
    brands?: string[];
    rental_plan?: string | number;
    price_min?: string | number;
    price_max?: string | number;
    userID: number | null;
    range_filters?: RangeFilter[];
    match_filters?: MatchFilter[];
    sort?: string;
    mixable?: string | boolean;
    in_stock?: string | boolean;
};

type ProductFilterEventProperty = {
    store: number;
    orderID?: string;
    brands?: string[];
    rental_plan?: number;
    price_min?: number;
    price_max?: number;
    userID: number | null;
    specs?: (RangeFilter | MatchFilter)[];
    is_mixable: boolean;
    availability: boolean;
};

type ProductFilterEventPayload = {
    eventLabel: string;
    data: FilterObjectType;
};

const handleStringOrBoolean = (value: string | boolean): boolean =>
    typeof value === 'string' ? value === 'true' : Boolean(value);

const formatEventProperty = ({
    store,
    orderID,
    brands = [],
    rental_plan: rentalPlan = DEFAULT_VALUE_RENTAL_PLAN,
    mixable = DEFAULT_VALUE_MIXABLE,
    in_stock: availability = DEFAULT_VALUE_IN_STOCK,
    price_min: priceMin,
    price_max: priceMax,
    userID = null,
    range_filters: rangeFilters = [],
    match_filters: matchFilters = [],
}: FilterObjectType): ProductFilterEventProperty => {
    const hasSpecs =
        rangeFilters.some(({ values }) => values.length > 0) ||
        matchFilters.some(({ values }) => values.length > 0);
    const hasBrands = brands.length > 0;

    return {
        store,
        userID,
        is_mixable: handleStringOrBoolean(mixable),
        availability: handleStringOrBoolean(availability),
        rental_plan: Number(rentalPlan),
        ...(orderID ? { orderID } : {}),
        ...(hasBrands ? { brands } : {}),
        ...(priceMin ? { price_min: Number(priceMin) / 100 } : {}),
        ...(priceMax ? { price_max: Number(priceMax) / 100 } : {}),
        ...(hasSpecs ? { specs: [...rangeFilters, ...matchFilters] } : {}),
    };
};

const formatEventAction = (eventLabel: string): string =>
    `${camelcase(eventLabel.concat('Filter'))}`;

export const sendProductFiltersEvent = (payload: ProductFilterEventPayload): void => {
    const { eventLabel, data } = payload;

    const eventAction =
        {
            price: 'priceFilter',
            brands: 'brandFilter',
            in_stock: 'availabilityFilter',
            rental_plan: 'rentalPlanFilter',
            mixable: 'mixableFilter',
        }[eventLabel] || formatEventAction(eventLabel);

    sendStructEvent({
        eventCategory: 'filterProducts',
        eventAction,
        eventLabel,
        eventProperty: formatEventProperty(data),
    });
};
